type FsDoc = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void>;
};
type FsEl = Element & {
  webkitRequestFullscreen?: () => Promise<void>;
};

function fsDoc(): FsDoc {
  return document as FsDoc;
}

export function isFullscreen(): boolean {
  const d = fsDoc();
  return !!(d.fullscreenElement || d.webkitFullscreenElement);
}

export function fullscreenAvailable(): boolean {
  const el = document.documentElement as FsEl;
  return !!(el.requestFullscreen || el.webkitRequestFullscreen);
}

export function isIOS(): boolean {
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ маскируется под Mac, но имеет touch
  return ua.includes('Mac') && navigator.maxTouchPoints > 1;
}

export function isStandalonePWA(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  if (nav.standalone) return true;
  return window.matchMedia('(display-mode: standalone)').matches
      || window.matchMedia('(display-mode: fullscreen)').matches;
}

/** Кнопку имеет смысл показывать если: API доступен ИЛИ iOS-в-браузере (там покажем инструкцию). */
export function fullscreenUseful(): boolean {
  if (isStandalonePWA()) return false;
  return fullscreenAvailable() || isIOS();
}

export async function enterFullscreen(): Promise<void> {
  if (isFullscreen()) return;
  const el = document.documentElement as FsEl;
  if (el.requestFullscreen) {
    await el.requestFullscreen({ navigationUI: 'hide' }).catch(() => el.requestFullscreen!());
  } else if (el.webkitRequestFullscreen) {
    await el.webkitRequestFullscreen();
  }
}

export async function exitFullscreen(): Promise<void> {
  if (!isFullscreen()) return;
  const d = fsDoc();
  if (d.exitFullscreen) await d.exitFullscreen();
  else if (d.webkitExitFullscreen) await d.webkitExitFullscreen();
}

export async function toggleFullscreen(): Promise<void> {
  if (isFullscreen()) await exitFullscreen();
  else await enterFullscreen();
}

export function onFullscreenChange(handler: () => void): () => void {
  document.addEventListener('fullscreenchange', handler);
  document.addEventListener('webkitfullscreenchange', handler);
  return () => {
    document.removeEventListener('fullscreenchange', handler);
    document.removeEventListener('webkitfullscreenchange', handler);
  };
}
